/**
 * client/src/lib/bankHealthDateFilter.ts
 *
 * Pure, presentation-layer logic for the Bank Health "Custom" date filter.
 *
 * The Custom filter has two modes:
 *   - "month" — a single Specific Month selection; From and To are always the
 *     same value (they collapse to one month).
 *   - "range" — a From/To window; From must be <= To or the range is invalid.
 *
 * The selection is persisted to localStorage so it survives navigation. A
 * subtle bug existed where a persisted month-mode selection could hydrate with
 * mismatched From/To bounds (e.g. left over from a prior range selection),
 * which would silently apply stale range filtering with no error shown. The
 * collapse logic lives here (`normalizeCustomBounds`) so both the hydration
 * path and the runtime mode switch use one source of truth.
 *
 * This module is PURE — it never touches React. The persistence helpers accept
 * a `Storage`-like object so they can be exercised in a node test environment
 * with an in-memory stub, and default to the browser `localStorage`.
 */

export type DateRangeOption = "3mo" | "qtr" | "6mo" | "ytd" | "all" | "custom";
export type CustomMode = "range" | "month";

export interface BankHealthDateFilterState {
  dateRange: DateRangeOption;
  customFrom: string; // "YYYY-MM"
  customTo: string; // "YYYY-MM"
  customMode: CustomMode;
}

export interface BankStatementPeriodLike {
  accountPeriodStart?: string | null;
  accountPeriodEnd?: string | null;
  createdAt?: string | Date | null;
}

export const BANK_HEALTH_DATE_FILTER_KEYS = {
  dateRange: "bankHealth.dateRange",
  customFrom: "bankHealth.customFrom",
  customTo: "bankHealth.customTo",
  customMode: "bankHealth.customMode",
} as const;

const VALID_DATE_RANGES: ReadonlySet<string> = new Set<DateRangeOption>([
  "3mo",
  "qtr",
  "6mo",
  "ytd",
  "all",
  "custom",
]);

/** Minimal subset of the DOM `Storage` interface this module needs. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function formatYearMonth(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function calendarDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const date = new Date(Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  ));
  return date.getUTCFullYear() === Number(match[1])
    && date.getUTCMonth() === Number(match[2]) - 1
    && date.getUTCDate() === Number(match[3])
    ? date
    : null;
}

function monthKeyFromDateValue(value: string | Date | null | undefined): string | null {
  if (!value) return null;
  const isoMatch = typeof value === "string" ? value.match(/^(\d{4})-(\d{2})/) : null;
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

/**
 * Assign a statement to the calendar month containing most of its period.
 * This handles normal cross-month dates (May 30–June 30) as well as an
 * accidental next-month end date (June 1–July 1) without relying on a filename.
 * Ties intentionally resolve to the ending month.
 */
export function getBankStatementMonthKey(
  statement: BankStatementPeriodLike,
): string | null {
  const start = calendarDate(statement.accountPeriodStart);
  const end = calendarDate(statement.accountPeriodEnd);

  if (start && end && end >= start) {
    const monthDays = new Map<string, number>();
    const cursor = new Date(start);
    const maxDays = 400;
    let days = 0;
    while (cursor <= end && days < maxDays) {
      const key = `${cursor.getUTCFullYear()}-${String(cursor.getUTCMonth() + 1).padStart(2, "0")}`;
      monthDays.set(key, (monthDays.get(key) ?? 0) + 1);
      cursor.setUTCDate(cursor.getUTCDate() + 1);
      days += 1;
    }

    if (cursor > end) {
      const endingMonth = monthKeyFromDateValue(statement.accountPeriodEnd);
      return Array.from(monthDays.entries()).reduce(
        (winner, candidate) => (
          candidate[1] > winner[1]
            || (candidate[1] === winner[1] && candidate[0] === endingMonth)
              ? candidate
              : winner
        ),
      )[0];
    }
  }

  return monthKeyFromDateValue(statement.accountPeriodEnd)
    ?? monthKeyFromDateValue(statement.accountPeriodStart)
    ?? monthKeyFromDateValue(statement.createdAt);
}

/** Default From bound when nothing is persisted: six months before `now`. */
export function defaultCustomFrom(now: Date = new Date()): string {
  const d = new Date(now);
  d.setMonth(d.getMonth() - 6);
  return formatYearMonth(d);
}

/** Default To bound when nothing is persisted: the current month. */
export function defaultCustomTo(now: Date = new Date()): string {
  return formatYearMonth(now);
}

/**
 * In "month" mode From and To must always be the same value. Returns the bounds
 * with To collapsed onto From when in month mode; range mode is returned
 * unchanged. Pure — never mutates its input.
 */
export function normalizeCustomBounds(
  customMode: CustomMode,
  customFrom: string,
  customTo: string,
): { customFrom: string; customTo: string } {
  if (customMode === "month") {
    return { customFrom, customTo: customFrom };
  }
  return { customFrom, customTo };
}

/**
 * The range is invalid only in range mode, only when both bounds are present,
 * and only when From is strictly after To. Month mode is never invalid (its
 * bounds are collapsed), and a non-custom date range is never invalid.
 */
export function isCustomRangeInvalid(
  dateRange: DateRangeOption,
  customMode: CustomMode,
  customFrom: string,
  customTo: string,
): boolean {
  return (
    dateRange === "custom" &&
    customMode === "range" &&
    !!customFrom &&
    !!customTo &&
    customFrom > customTo
  );
}

/**
 * Years offered in the Custom dropdowns: every year present in the supplied
 * statement month keys ("YYYY-MM"), plus the current year and the years of the
 * currently-selected From/To bounds — so the Year <Select> always has a
 * matching option to display for whatever value is selected. Descending order.
 */
export function computeAvailableYears(
  statementMonthKeys: ReadonlyArray<string | null>,
  customFrom: string,
  customTo: string,
  now: Date = new Date(),
): string[] {
  const years = new Set<string>();
  for (const key of statementMonthKeys) {
    if (key) years.add(key.slice(0, 4));
  }
  years.add(String(now.getFullYear()));
  if (customFrom) years.add(customFrom.slice(0, 4));
  if (customTo) years.add(customTo.slice(0, 4));
  return Array.from(years).sort().reverse();
}

function readDateRange(raw: string | null): DateRangeOption {
  return raw && VALID_DATE_RANGES.has(raw) ? (raw as DateRangeOption) : "6mo";
}

function readCustomMode(raw: string | null): CustomMode {
  return raw === "month" || raw === "range" ? raw : "range";
}

/**
 * Hydrates the persisted filter state from storage, applying defaults for any
 * missing keys and collapsing month-mode bounds via `normalizeCustomBounds`.
 * This guarantees a month-mode selection never hydrates with stale, mismatched
 * From/To bounds.
 */
export function loadBankHealthDateFilter(
  storage: StorageLike,
  now: Date = new Date(),
): BankHealthDateFilterState {
  const dateRange = readDateRange(storage.getItem(BANK_HEALTH_DATE_FILTER_KEYS.dateRange));
  const customMode = readCustomMode(storage.getItem(BANK_HEALTH_DATE_FILTER_KEYS.customMode));
  const customFrom =
    storage.getItem(BANK_HEALTH_DATE_FILTER_KEYS.customFrom) || defaultCustomFrom(now);
  const customTo =
    storage.getItem(BANK_HEALTH_DATE_FILTER_KEYS.customTo) || defaultCustomTo(now);
  const normalized = normalizeCustomBounds(customMode, customFrom, customTo);
  return { dateRange, customMode, customFrom: normalized.customFrom, customTo: normalized.customTo };
}

/** Persists the current filter state to storage. */
export function persistBankHealthDateFilter(
  storage: StorageLike,
  state: BankHealthDateFilterState,
): void {
  storage.setItem(BANK_HEALTH_DATE_FILTER_KEYS.dateRange, state.dateRange);
  storage.setItem(BANK_HEALTH_DATE_FILTER_KEYS.customFrom, state.customFrom);
  storage.setItem(BANK_HEALTH_DATE_FILTER_KEYS.customTo, state.customTo);
  storage.setItem(BANK_HEALTH_DATE_FILTER_KEYS.customMode, state.customMode);
}
